import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { notify } from '@/lib/notify'
import { TableCard } from '@/components/common/table/TableCard'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { WalletTypesDataTable } from './WalletTypesDataTable'
import type { DetailedWalletTypeResponse } from '@/api/model'
import {
  getGetApiCompaniesWalletTypesQueryKey,
  useDeleteApiCompaniesWalletTypesWalletTypeId,
  useGetApiCompaniesWalletTypes,
} from '@/api/generated/company-management-user/company-management-user'

interface Props {
  onEdit: (walletType: DetailedWalletTypeResponse) => void
}

export function WalletTypesTable({ onEdit }: Props) {
  const { t } = useTranslation(['wallet-types', 'common'])
  const queryClient = useQueryClient()
  const [confirmWalletType, setConfirmWalletType] = useState<DetailedWalletTypeResponse | null>(null)

  const { data, isLoading, isError } = useGetApiCompaniesWalletTypes()
  const walletTypes = data?.data ?? []

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getGetApiCompaniesWalletTypesQueryKey() })

  const { mutate: deleteWalletType, isPending: isDeleting } = useDeleteApiCompaniesWalletTypesWalletTypeId({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.deleted'))
        setConfirmWalletType(null)
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('toasts.deleteError')),
    },
  })

  return (
    <>
      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom relative">
          <WalletTypesDataTable
            isLoading={isLoading}
            isError={isError}
            walletTypes={walletTypes}
            onEdit={onEdit}
            onDelete={setConfirmWalletType}
          />
        </div>
      </TableCard>

      <ConfirmDialog
        open={!!confirmWalletType}
        title={t('list.delete.title')}
        description={t('list.delete.description', { name: confirmWalletType?.name ?? '' })}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
        onConfirm={() => confirmWalletType?.id && deleteWalletType({ walletTypeId: confirmWalletType.id })}
        onClose={() => setConfirmWalletType(null)}
      />
    </>
  )
}
